
import React, { useEffect } from 'react';
import { useLanguage, Language } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Check, Globe, Loader2, RotateCw } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/use-toast';
import ImageOptimizer from '@/components/ImageOptimizer';

interface LanguageSelectorProps {
  variant?: 'default' | 'landing';
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ variant = 'default' }) => {
  const { language, setLanguage, t, isLoading, reloadTranslations, translations } = useLanguage();

  // Debug translations loading with more robust error handling
  useEffect(() => {
    console.log('LanguageSelector - Current language:', language);
    console.log('LanguageSelector - Translation keys available:', Object.keys(translations).length);
    
    // Only attempt to check translation keys if we have translations loaded
    if (Object.keys(translations).length > 0) {
      // Check for key translations
      const criticalKeys = [
        'stats.h2h', 
        'engine.marketOddsAnalysis',
        'engine.statisticalModel',
        'engine.bettingRecommendations',
        'match.details',
        'match.summary',
        'match.backToLeague',
        'match.referee',
        'match.stadium',
        'match.city',
        'match.competition',
        'match.round',
        'match.status',
        'match.date'
      ];
      
      const missingKeys = criticalKeys.filter(key => !translations[key] || translations[key] === key);
      
      if (missingKeys.length > 0) {
        console.warn('LanguageSelector - Missing critical translation keys:', missingKeys);
      }
    }
  }, [language, translations]);

  const languages: { code: Language; name: string; nativeName: string; flagSrc: string }[] = [
    { code: 'en', name: 'English', nativeName: 'English', flagSrc: '/flags/uk.svg' },
  ];

  const handleLanguageChange = async (langCode: Language) => {
    try {
      await setLanguage(langCode);
      const langName = languages.find(l => l.code === langCode)?.name || langCode;
      toast({
        title: t('app.language'),
        description: `${t('app.language')} changed to ${langName}`,
      });
      
      // Check for critical translations right after language change with better error handling
      setTimeout(() => {
        if (!translations) return;
        
        const criticalKeys = [
          'stats.h2h', 
          'engine.marketOddsAnalysis',
          'engine.statisticalModel',
          'engine.bettingRecommendations',
          'match.details'
        ];
        
        const missingKeys = criticalKeys.filter(key => !translations[key] || translations[key] === key);
        if (missingKeys.length > 0) {
          console.warn(`LanguageSelector - After language change: missing ${missingKeys.length} critical keys`);
          // Auto-reload if we're missing critical keys
          handleRetryLoad();
        }
      }, 500);
    } catch (error) {
      console.error('Failed to change language:', error);
      toast({
        title: 'Error',
        description: 'Failed to change language. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleRetryLoad = async () => {
    try {
      await reloadTranslations();
      toast({
        title: 'Success',
        description: 'Translations reloaded successfully',
      });
      
      // Verify translations were loaded with better error handling
      setTimeout(() => {
        if (!translations) return;
        
        const criticalKeys = [
          'stats.h2h', 
          'engine.marketOddsAnalysis',
          'engine.statisticalModel',
          'engine.bettingRecommendations',
          'match.details'
        ];
        
        const stillMissing = criticalKeys.filter(key => !translations[key] || translations[key] === key);
        
        if (stillMissing.length > 0) {
          console.error('LanguageSelector - Translations still missing after reload:', stillMissing);
          toast({
            title: 'Warning',
            description: `Some translations still missing (${stillMissing.length} keys)`,
            variant: 'destructive',
          });
        } else {
          console.log('LanguageSelector - All critical translations loaded successfully');
        }
      }, 500);
    } catch (error) {
      console.error('Failed to reload translations:', error);
      toast({
        title: 'Error',
        description: 'Failed to reload translations. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant === 'landing' ? 'outline' : 'ghost'} 
          size="sm" 
          className={`h-8 w-8 px-0 flex items-center justify-center ${variant === 'landing' ? 'bg-background/80 hover:bg-background' : ''}`}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Globe className="h-4 w-4" />
          )}
          <span className="sr-only">{isLoading ? 'Loading language' : 'Select language'}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[200px] bg-background">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className="flex items-center justify-between py-2"
            disabled={isLoading}
          >
            <div className="flex items-center">
              <div className="w-6 h-6 mr-2 rounded-full overflow-hidden border border-muted flex items-center justify-center bg-muted/5">
                <ImageOptimizer 
                  src={lang.flagSrc} 
                  alt={`${lang.name} flag`} 
                  className="w-6 h-6 object-cover"
                  priority={true}
                  fallbackSrc="/flags/globe.svg" 
                />
              </div>
              <span className="truncate">
                {lang.nativeName}
              </span>
              <span className="ml-2 text-muted-foreground text-xs">
                ({lang.name})
              </span>
            </div>
            {language === lang.code && (
              isLoading ? 
                <Loader2 className="h-4 w-4 ml-2 animate-spin text-primary" /> : 
                <Check className="h-4 w-4 ml-2 text-primary" />
            )}
          </DropdownMenuItem>
        ))}
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          onClick={handleRetryLoad}
          className="text-xs text-muted-foreground"
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-3 w-3 mr-2 animate-spin" />
          ) : (
            <RotateCw className="h-3 w-3 mr-2" />
          )}
          Reload translations
        </DropdownMenuItem>

        <DropdownMenuItem 
          className="text-xs text-muted-foreground"
          disabled={true}
        >
          <span className="text-xs opacity-70">
            Keys: {translations ? Object.keys(translations).length : 0}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
